import { PropsWithChildren, ReactNode, useRef, useState } from 'react';

import ConfirmActions from './ConfirmActions';
import { ConfirmAction } from './types';

export type ConfirmComponentProps = PropsWithChildren<{
  onCancel?: () => void;
  open: boolean;
  prompt: ReactNode;
}>;

const useConfirmBase = (loading?: boolean) => {
  const requestResolver = useRef<(value: boolean | PromiseLike<boolean>) => void>(null);
  const [shouldShow, setShouldShow] = useState(false);
  const [prompt, setPrompt] = useState<ReactNode>('');
  const [action, setAction] = useState<ConfirmAction>('ok');

  const request = (prompt: ReactNode, action: ConfirmAction = 'ok') => {
    setPrompt(prompt);
    setAction(action);

    return new Promise<boolean>(resolve => {
      requestResolver.current = resolve;
      setShouldShow(true);
    });
  };

  const handleOnCancel = () => {
    if (requestResolver.current) requestResolver.current(false);
    setShouldShow(false);
  };

  const handleOnConfirm = () => {
    if (requestResolver.current) requestResolver.current(true);
    setShouldShow(false);
  };

  return {
    actions: (
      <ConfirmActions
        action={action}
        loading={loading}
        onCancel={handleOnCancel}
        onConfirm={handleOnConfirm}
      />
    ),
    loading,
    onCancel: handleOnCancel,
    open: Boolean(shouldShow || loading),
    prompt,
    request,
  };
};

export default useConfirmBase;
